/**
 * Bigens scratch & reveal pop-up (T31, D50).
 *
 * 1. Triggers: after the section's delay, or on exit intent (desktop pointers only). Never while another dialog is
 *    open, never again once the prize is revealed and closed, and not for `snoozeDays` after a close before sign-up.
 *    Desktop opens a centred modal; mobile shows a small non-modal bottom panel and only opens the form (as a
 *    bottom sheet) when the shopper taps it. Signed up but not revealed yet: the same triggers show the scratch step
 *    (mobile: the panel first), and closing it before the reveal leaves the panel with the same prize.
 * 2. Submit: the prize is drawn by weighted random at submit and stored before anything is sent, so a retry or a
 *    reload never re-rolls. The native customer form posts with fetch; Shopify redirects to ?customer_posted=true on
 *    success. A bot challenge (or anything unexpected) falls back to a normal post through requestSubmit(), so
 *    Shopify's bot check sees a real submit event: the page then reloads with customer_posted=true and the stored
 *    prize is revealed. Every customer form on the page shares that posted/error state, so it only counts when this
 *    pop-up started the post (a sessionStorage flag).
 * 3. Scratch: a canvas ball over the result, scratched with any pointer. "Reveal" does the same for keyboard,
 *    screen-reader and reduced-motion users. The result is announced and focus moves to "Copy".
 *
 * State lives in localStorage under STORAGE_KEY. The theme editor ignores it, opens on section select and never posts.
 */

const STORAGE_KEY = 'bigens:popup-scratch';
const PENDING_KEY = 'bigens:popup-scratch:pending';
const HIDDEN_KEY = 'bigens:popup-scratch:hidden';
const DAY = 24 * 60 * 60 * 1000;
const RETRY_DELAY = 5000;
const REVEAL_AT = 0.45;
const MOBILE = '(max-width: 749px)';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** A uniform random number in [0, 1) from the crypto API. */
function secureRandom() {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return /** @type {number} */ (values[0]) / 2 ** 32;
}

/**
 * Picks a prize index with probability weight / total weight. Prizes without a code or weight never win.
 * @param {{ percent: number, code: string, weight: number }[]} prizes
 * @param {() => number} [random] - Returns a number in [0, 1).
 * @returns {number} The prize index, or -1 when no prize can win.
 */
export function pickPrize(prizes, random = secureRandom) {
  const weights = prizes.map((prize) => (prize.code && prize.weight > 0 ? prize.weight : 0));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (total <= 0) return -1;

  let draw = random() * total;
  let last = -1;
  for (let i = 0; i < weights.length; i++) {
    const weight = /** @type {number} */ (weights[i]);
    if (weight <= 0) continue;
    last = i;
    draw -= weight;
    if (draw < 0) return i;
  }
  return last;
}

/**
 * @typedef {object} PopupState
 * @property {number} [prize] - Index of the drawn prize (drawn once, at the first submit).
 * @property {boolean} [signedUp] - The customer form succeeded.
 * @property {boolean} [revealed] - The ball was scratched or revealed.
 * @property {boolean} [done] - Closed (or "Shop now") after the reveal: never show again.
 * @property {number} [snoozedUntil] - Closed before signing up: no auto-open until this time.
 */

/**
 * sessionStorage helpers (blocked storage just means no flag).
 * @param {string} key
 * @param {string | null} value - null removes the key.
 */
function setSession(key, value) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked.
  }
}

/** @param {string} key */
function getSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

class BigensPopupScratch extends HTMLElement {
  /** @type {PopupState} */
  state = {};

  /** @type {number | undefined} */
  timer;

  connectedCallback() {
    const prizesData = this.querySelector('[data-popup-prizes]')?.textContent;
    this.prizes = prizesData ? JSON.parse(prizesData) : [];
    if (pickPrize(this.prizes, () => 0) === -1) return;

    this.designMode = this.hasAttribute('data-design-mode');
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('[data-popup-dialog]'));
    this.teaser = /** @type {HTMLElement} */ (this.querySelector('[data-popup-teaser]'));
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    this.tags = /** @type {HTMLInputElement} */ (this.querySelector('[data-popup-tags]'));
    this.baseTags = this.tags.value;
    this.error = /** @type {HTMLElement} */ (this.querySelector('[data-popup-error]'));
    this.submitButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-popup-submit]'));
    this.canvas = /** @type {HTMLCanvasElement} */ (this.querySelector('[data-popup-canvas]'));
    this.hint = /** @type {HTMLElement} */ (this.querySelector('[data-popup-hint]'));
    this.revealButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-popup-reveal]'));
    this.scratchText = /** @type {HTMLElement} */ (this.querySelector('[data-popup-scratch-text]'));
    this.status = /** @type {HTMLElement} */ (this.querySelector('[data-popup-status]'));

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.querySelectorAll('[data-popup-close]').forEach((button) =>
      button.addEventListener('click', () => this.dismiss(), { signal })
    );
    this.querySelector('[data-popup-expand]')?.addEventListener(
      'click',
      () => this.open(this.state.signedUp ? 'scratch' : 'form'),
      { signal }
    );
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.dismiss();
      },
      { signal }
    );
    // A click on the backdrop lands on the dialog element itself.
    this.dialog.addEventListener('click', (event) => event.target === this.dialog && this.dismiss(), { signal });
    this.dialog.addEventListener('keydown', (event) => event.key === 'Tab' && this.trapFocus(event), { signal });
    // Its own controller: the native fallback removes it, so requestSubmit() posts the form normally.
    this.submitListener = new AbortController();
    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.submitListener.signal });
    this.revealButton.addEventListener('click', () => this.reveal(true), { signal });
    this.querySelectorAll('[data-popup-copy]').forEach((button) =>
      button.addEventListener('click', () => this.copy(/** @type {HTMLButtonElement} */ (button)), { signal })
    );
    this.querySelectorAll('[data-popup-shop]').forEach((link) =>
      link.addEventListener('click', () => this.save({ done: true }), { signal })
    );

    if (this.designMode) {
      this.listenToEditor(signal);
      return;
    }

    this.state = this.load();
    this.resume(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.submitListener?.abort();
    clearTimeout(this.timer);
    if (this.dialog?.open) this.dialog.close();
  }

  /** @param {AbortSignal} signal */
  resume(signal) {
    const { state } = this;
    const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';

    // Back from this pop-up's own native post (the fetch fallback). The posted/error markers are shared by every
    // customer form (footer newsletter, waitlist), so without the flag they belong to another form.
    const pending = getSession(PENDING_KEY) === 'true';
    setSession(PENDING_KEY, null);
    if (pending && state.prize !== undefined && !state.signedUp) {
      if (posted && this.querySelector('[data-popup-posted]')) {
        this.save({ signedUp: true });
        this.open('scratch');
        return;
      }
      if (!this.error.hidden) {
        this.open('form');
        return;
      }
    }
    if (!pending) {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    if (state.done) return;
    if (state.signedUp) {
      // Not revealed yet: the panel comes back on the next visit, after the delay.
      if (getSession(HIDDEN_KEY) === 'true') return;
    } else if (state.snoozedUntil && state.snoozedUntil > Date.now()) {
      return;
    }

    const delay = Number(this.dataset.delay || 15) * 1000;
    this.timer = window.setTimeout(() => this.autoOpen(), delay);

    if (this.dataset.exitIntent === 'true' && matchMedia(FINE_POINTER).matches && !matchMedia(MOBILE).matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.autoOpen();
        },
        { signal }
      );
    }
  }

  /** @param {AbortSignal} signal */
  listenToEditor(signal) {
    const isThis = (/** @type {Event} */ event) =>
      /** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId;
    document.addEventListener('shopify:section:select', (event) => isThis(event) && this.open('form'), { signal });
    document.addEventListener('shopify:section:deselect', (event) => isThis(event) && this.close(), { signal });
  }

  autoOpen() {
    clearTimeout(this.timer);
    if (this.dialog.open || !this.teaser.hidden || this.autoOpened) return;

    // Don't stack on top of the cart drawer, search or another dialog; try again a little later.
    if (document.querySelector('dialog[open]')) {
      this.timer = window.setTimeout(() => this.autoOpen(), RETRY_DELAY);
      return;
    }

    this.autoOpened = true;
    if (matchMedia(MOBILE).matches) {
      this.teaser.hidden = false;
    } else {
      this.open(this.state.signedUp ? 'scratch' : 'form');
    }
  }

  /** @param {'form' | 'scratch'} step */
  open(step) {
    this.teaser.hidden = true;
    this.showStep(step);
    if (!this.dialog.open) {
      const active = document.activeElement;
      this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
      this.dialog.showModal();
    }
    if (step === 'scratch') this.setUpScratch();
  }

  /** @param {'form' | 'scratch'} step */
  showStep(step) {
    this.dialog.querySelectorAll('[data-popup-step]').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = panel.getAttribute('data-popup-step') !== step;
    });
    const labelId = step === 'form' ? this.dialog.dataset.labelForm : this.dialog.dataset.labelScratch;
    if (labelId) this.dialog.setAttribute('aria-labelledby', labelId);
    this.step = step;
  }

  /**
   * Keeps Tab inside the open step. A modal dialog makes the page inert, but browsers still let Tab leave for the
   * address bar (or, in some, the page's first link), so wrap explicitly.
   * @param {KeyboardEvent} event
   */
  trapFocus(event) {
    const focusables = [
      ...this.dialog.querySelectorAll(`[data-popup-step="${this.step}"] :is(a[href], button, input, [tabindex="0"])`),
    ].filter((element) => /** @type {HTMLElement} */ (element).offsetParent !== null && !element.matches(':disabled'));
    const first = /** @type {HTMLElement | undefined} */ (focusables[0]);
    const last = /** @type {HTMLElement | undefined} */ (focusables[focusables.length - 1]);
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.dialog.contains(active) || active === this.dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  close() {
    this.teaser.hidden = true;
    if (this.dialog.open) this.dialog.close();
    if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    this.returnFocus = null;
  }

  /** The shopper closes the pop-up or the panel (button, Esc or backdrop). */
  dismiss() {
    if (!this.designMode) {
      if (this.state.revealed) {
        this.save({ done: true });
      } else if (this.state.signedUp) {
        // Not revealed yet: closing the dialog leaves the small panel with the same prize; closing the panel hides it
        // for this visit.
        if (this.dialog.open) {
          this.close();
          this.teaser.hidden = false;
          return;
        }
        setSession(HIDDEN_KEY, 'true');
      } else {
        this.save({ snoozedUntil: Date.now() + Number(this.dataset.snoozeDays || 7) * DAY });
      }
    }
    this.close();
  }

  /** @param {SubmitEvent} event */
  async onSubmit(event) {
    event.preventDefault();

    if (this.state.prize === undefined) {
      const prize = pickPrize(this.prizes);
      if (this.designMode) this.state.prize = prize;
      else this.save({ prize });
    }
    const prize = this.prizes[/** @type {number} */ (this.state.prize)];
    this.tags.value = `${this.baseTags},scratch-${prize.percent}`;

    // The editor never posts a real sign-up; it previews the next step.
    if (this.designMode) {
      this.onSignedUp();
      return;
    }

    this.error.hidden = true;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });
      const url = new URL(response.url);

      if (url.searchParams.get('customer_posted') === 'true') {
        this.onSignedUp();
        return;
      }

      if (!url.pathname.includes('/challenge')) {
        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const error = page.getElementById(this.error.id);
        if (error && !error.hidden && error.textContent?.trim()) {
          this.error.textContent = error.textContent.trim();
          this.error.hidden = false;
          this.form.querySelector('input[type="email"]')?.setAttribute('aria-invalid', 'true');
          return;
        }
      }
      this.submitNatively();
    } catch {
      this.submitNatively();
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  /** A normal post with a real submit event (Shopify's bot check listens for it); this pop-up's handler steps aside. */
  submitNatively() {
    setSession(PENDING_KEY, 'true');
    this.submitListener?.abort();
    this.form.requestSubmit();
  }

  onSignedUp() {
    if (!this.designMode) this.save({ signedUp: true });
    this.open('scratch');
    this.dialog.querySelector('[data-popup-step="scratch"] h2')?.setAttribute('tabindex', '-1');
    /** @type {HTMLElement | null} */ (this.dialog.querySelector('[data-popup-step="scratch"] h2'))?.focus();
  }

  setUpScratch() {
    const index = /** @type {number} */ (this.state.prize);
    this.querySelectorAll('[data-popup-result]').forEach((result) => {
      /** @type {HTMLElement} */ (result).hidden = result.getAttribute('data-popup-result') !== `${index}`;
    });

    if (this.state.revealed) {
      this.reveal(false);
      return;
    }
    if (this.scratchReady) return;
    this.scratchReady = true;
    this.paintBall();

    const { canvas } = this;
    const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d', { willReadFrequently: true }));
    /** @type {{ x: number, y: number } | null} */
    let last = null;
    let moves = 0;

    const point = (/** @type {PointerEvent} */ event) => {
      const rect = canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    canvas.addEventListener('pointerdown', (event) => {
      if (this.state.revealed) return;
      canvas.setPointerCapture(event.pointerId);
      this.hint.hidden = true;
      last = point(event);
      scratch(last, last);
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!last) return;
      const next = point(event);
      scratch(last, next);
      last = next;
      if (++moves % 12 === 0) this.checkScratched();
    });
    const stop = () => {
      if (!last) return;
      last = null;
      this.checkScratched();
    };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);

    const scratch = (/** @type {{ x: number, y: number }} */ from, /** @type {{ x: number, y: number }} */ to) => {
      const size = canvas.getBoundingClientRect().width;
      context.globalCompositeOperation = 'destination-out';
      context.lineWidth = size * 0.16;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x + 0.01, to.y);
      context.stroke();
    };
  }

  /** Draws the scratch surface: the brand ball with its shine (snippets/bigens-shine.liquid geometry). */
  paintBall() {
    const { canvas } = this;
    const size = canvas.getBoundingClientRect().width || 192;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(size * ratio);
    canvas.height = Math.round(size * ratio);
    const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d', { willReadFrequently: true }));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const styles = getComputedStyle(this);
    const ball = styles.getPropertyValue('--bigens-red').trim() || '#d61818';
    const shine = styles.getPropertyValue('--bigens-off-white').trim() || '#fafafa';
    const unit = size / 100;

    context.globalCompositeOperation = 'source-over';
    context.fillStyle = ball;
    context.beginPath();
    context.arc(50 * unit, 50 * unit, 50 * unit, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = shine;
    context.fillStyle = shine;
    context.lineWidth = 4 * unit;
    context.lineCap = 'round';
    context.beginPath();
    context.arc(50 * unit, 50 * unit, 41 * unit, (-44 * Math.PI) / 180, (-16 * Math.PI) / 180);
    context.stroke();
    context.beginPath();
    context.arc(90.94 * unit, 47.85 * unit, 2.2 * unit, 0, Math.PI * 2);
    context.fill();
  }

  /** Reveals once enough of the ball is scratched away. Samples a grid of pixels inside the circle. */
  checkScratched() {
    const { canvas } = this;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context || !canvas.width) return;
    const { width, height } = canvas;
    const data = context.getImageData(0, 0, width, height).data;
    const radius = width / 2;
    const step = Math.max(2, Math.round(width / 60));
    let inside = 0;
    let cleared = 0;
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        if ((x - radius) ** 2 + (y - radius) ** 2 > radius ** 2) continue;
        inside++;
        if (/** @type {number} */ (data[(y * width + x) * 4 + 3]) < 128) cleared++;
      }
    }
    if (inside && cleared / inside >= REVEAL_AT) this.reveal(false);
  }

  /** @param {boolean} moveFocus - True when "Reveal" was pressed: focus moves to "Copy" (the button disappears). */
  reveal(moveFocus) {
    const index = /** @type {number} */ (this.state.prize);
    const firstTime = !this.state.revealed;
    if (this.designMode) this.state.revealed = true;
    else this.save({ revealed: true });

    const result = this.querySelector(`[data-popup-result="${index}"]`);
    result?.removeAttribute('aria-hidden');
    this.hint.hidden = true;
    this.revealButton.hidden = true;
    this.scratchText.hidden = true;

    if (firstTime && !matchMedia(REDUCED_MOTION).matches) {
      this.canvas.classList.add('is-revealed');
      this.canvas.addEventListener('transitionend', () => (this.canvas.hidden = true), { once: true });
    } else {
      this.canvas.hidden = true;
    }

    const win = /** @type {HTMLElement | null} */ (this.querySelector(`[data-popup-win="${index}"]`));
    if (!win) return;
    win.hidden = false;
    if (firstTime) this.status.textContent = win.dataset.wonText || '';
    if (moveFocus) /** @type {HTMLElement | null} */ (win.querySelector('[data-popup-copy]'))?.focus();
  }

  /** @param {HTMLButtonElement} button */
  async copy(button) {
    const code = button.dataset.popupCopy || '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // No clipboard access: select the code so the shopper can copy it by hand.
      const value = button.parentElement?.querySelector('.bigens-popup__code-value');
      if (value) window.getSelection()?.selectAllChildren(value);
      return;
    }
    const label = button.textContent;
    button.textContent = button.dataset.copiedText || label;
    this.status.textContent = button.dataset.copiedText || '';
    window.setTimeout(() => (button.textContent = label), 2000);
  }

  /** @returns {PopupState} */
  load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  /** @param {PopupState} changes */
  save(changes) {
    this.state = { ...this.state, ...changes };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Storage blocked (private mode): the state lasts for this page only.
    }
  }
}

if (!customElements.get('bigens-popup-scratch')) {
  customElements.define('bigens-popup-scratch', BigensPopupScratch);
}
